import React from 'react';
import { useTranslation } from 'react-i18next';
import { Table, TableContainer, TableRow, TableBody, Box } from '@mui/material';

import { TableCell } from 'common/components/Table';
import { Typography } from 'common/components/Typography';
import { BoxLoader } from 'common/components/Loader';
import {
    IBasicUserFragment,
    IContentTypeValue,
    IPortfolioItemChildFragment,
    usePortfolioItemChildrenQuery,
} from 'graphql/types';
import { useGlobalDrawer } from 'common/hooks/useGlobalDrawer';
import { IGlobalDrawerType } from 'common/types';
import { ReceivedCertificatePortfolioItemTableRow } from 'skill/components/ReceivedCertificatePortfolioItemTable/ReceivedCertificatePortfolioItemTableRow';
import { useApolloError } from 'common/hooks/useApolloError';

interface IProps {
    portfolioItemId: string;
    user?: IBasicUserFragment;
}

export const ReceivedCertificatePortfolioItemTable = ({
    portfolioItemId,
    user,
}: IProps) => {
    const [translate] = useTranslation();
    const { showApolloError } = useApolloError();
    const { openGlobalDrawer } = useGlobalDrawer();

    const { data, loading, refetch } = usePortfolioItemChildrenQuery({
        variables: { id: portfolioItemId },
        onError: showApolloError,
    });

    if (loading) return <BoxLoader />;

    const items =
        (data?.portfolioItem?.children as IPortfolioItemChildFragment[]) || [];

    if (!loading && !items.length) {
        return (
            <Box sx={{ mt: 3.5 }}>
                <Typography sx={{ mb: 0 }}>
                    {translate('report.noSkillsReports')}
                </Typography>
            </Box>
        );
    }

    return (
        <TableContainer>
            <Table>
                <TableBody>
                    <React.Fragment>
                        <TableRow>
                            <TableCell colSpan={3} sx={{ pt: 3 }}>
                                <Typography fontWeight={700} variant="body2">
                                    {translate(
                                        'skillDrawer.receivedCertificateReportTable.title',
                                        { count: items.length }
                                    )}
                                </Typography>
                            </TableCell>
                        </TableRow>

                        {items.map((item) => (
                            <ReceivedCertificatePortfolioItemTableRow
                                item={item}
                                key={item.id}
                                onClick={() => {
                                    if (!user) return;

                                    const isReceivedCertificate =
                                        item.relatedType ===
                                        IContentTypeValue.ReceivedCertificate;

                                    openGlobalDrawer({
                                        type: IGlobalDrawerType.ReceivedCertificate,
                                        itemId: item.id,
                                        props: {
                                            title: item.title,
                                            user,
                                            receivedCertificateId:
                                                isReceivedCertificate
                                                    ? item.relatedId
                                                    : undefined,
                                            certificateId:
                                                !isReceivedCertificate
                                                    ? item.relatedId
                                                    : undefined,
                                            onGenerateCertificate: () => {
                                                refetch();
                                            },
                                        },
                                    });
                                }}
                            />
                        ))}
                    </React.Fragment>
                </TableBody>
            </Table>
        </TableContainer>
    );
};
